import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import api from '../services/api';

export interface BookedSlot {
  id: number;
  worker_id: number;
  scraped_job_id: number;
  start_time: string;
  end_time: string;
  status: 'tentative' | 'confirmed' | 'canceled';
  created_at: string;
  title?: string;
  company?: string;
  location?: string;
}

interface ScheduleState {
  slots: BookedSlot[];
  upcoming: BookedSlot[];
  loading: boolean;
  error: string | null;
}

const initialState: ScheduleState = {
  slots: [],
  upcoming: [],
  loading: false,
  error: null,
};

export const fetchSchedule = createAsyncThunk(
  'schedule/fetchSchedule',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/worker-profile/schedule');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch schedule');
      }
      return rejectWithValue('Failed to fetch schedule');
    }
  }
);

export const fetchUpcoming = createAsyncThunk(
  'schedule/fetchUpcoming',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/worker-profile/schedule/upcoming');
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to fetch upcoming');
      }
      return rejectWithValue('Failed to fetch upcoming');
    }
  }
);

export const cancelSlot = createAsyncThunk(
  'schedule/cancelSlot',
  async (slotId: number, { rejectWithValue }) => {
    try {
      const response = await api.post(`/worker-profile/schedule/${slotId}/cancel`);
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data?.message || 'Failed to cancel slot');
      }
      return rejectWithValue('Failed to cancel slot');
    }
  }
);

const scheduleSlice = createSlice({
  name: 'schedule',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchSchedule.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSchedule.fulfilled, (state, action) => {
        state.loading = false;
        state.slots = action.payload;
      })
      .addCase(fetchSchedule.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchUpcoming.pending, (state) => { state.loading = true; })
      .addCase(fetchUpcoming.fulfilled, (state, action) => {
        state.loading = false;
        state.upcoming = action.payload;
      })
      .addCase(fetchUpcoming.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; })
      .addCase(cancelSlot.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(cancelSlot.fulfilled, (state, action) => {
        state.loading = false;
        const updated = action.payload;
        state.slots = state.slots.map(s => s.id === updated.id ? updated : s);
        state.upcoming = state.upcoming.filter(s => s.id !== updated.id);
      })
      .addCase(cancelSlot.rejected, (state, action) => { state.loading = false; state.error = action.payload as string; });
  },
});

export default scheduleSlice.reducer;
